import { useNavigate } from 'react-router-dom'

import { Box, Button, Popover, Row, Text, useToggle } from '@factorialco/deprecated-design-system'
import { F0Icon } from '@factorialco/f0-react'
import { InfoCircleLine } from '@factorialco/f0-react/icons/app'
import {
  ContractsSettingPageDocument,
  CreateWorkingConditionDocument,
  QueryWorkingConditionsRolesAndLocationsDocument,
  SimpleError,
  StructuredError,
} from 'generated/resources/graphql'

import Modal from 'modules/core/components/Modal'
import Selector from 'modules/core/components/Selector'
import TextArea from 'modules/core/components/TextArea'
import TextField from 'modules/core/components/TextField'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { FormErrors, useForm } from 'modules/core/hooks/useForm'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useQuery } from 'modules/core/hooks/useQuery'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

type Props = {
  onClose: () => void
}

type WorkConditionFormData = {
  name: string
  description?: string
  role?: string | null
  workplace?: string | null
}

const CreateWorkingConditionModal = ({ onClose }: Props) => {
  const { company } = useCurrentSession()
  const { navigation } = useNavigation()
  const navigate = useNavigate()

  const [open, toggle] = useToggle(false)

  const { data: workplacesAndRoles } = useQuery(QueryWorkingConditionsRolesAndLocationsDocument, {
    variables: {},
  })

  const allRolesLabel = i18n.t(
    'company_settings.general.employment_data.work_conditions.fields.role.all_label'
  )
  const roleOptions = [
    <Selector.Option key="role-0" label={allRolesLabel} searchValue={allRolesLabel} value={null}>
      {allRolesLabel}
    </Selector.Option>,
    ...(workplacesAndRoles?.jobCatalog?.levels?.map((role) => {
      const roleLevelLabel = role.name !== '__default__' ? ` / ${role.name}` : ''
      const roleLabel = `${role.roleName}${roleLevelLabel}`
      return (
        <Selector.Option
          key={`role-${role.id}`}
          label={roleLabel}
          searchValue={roleLabel}
          value={role.id}
        >
          {roleLabel}
        </Selector.Option>
      )
    }) || []),
  ]

  const anyWorkplaceLabel = i18n.t(
    'company_settings.general.employment_data.work_conditions.fields.workplace.all_label'
  )
  const workplaceOptions = [
    <Selector.Option
      key="workplace-0"
      label={anyWorkplaceLabel}
      searchValue={anyWorkplaceLabel}
      value={null}
    >
      {anyWorkplaceLabel}
    </Selector.Option>,
    ...(workplacesAndRoles?.locations?.locations?.map((workplace) => (
      <Selector.Option
        key={`workplace-${workplace.id}`}
        label={workplace.name}
        searchValue={workplace.name}
        value={workplace.id}
      >
        {workplace.name}
      </Selector.Option>
    )) || []),
  ]

  const [createWorkingCondition] = useMutation(CreateWorkingConditionDocument, {
    extractorFn: (data) => data.workingConditionsMutations.createWorkingconditionsVersion,
    refetchQueries: [ContractsSettingPageDocument],
  })

  const getErrorMessage = (errors: readonly (SimpleError | StructuredError)[]) => {
    const error = errors?.[0]
    if (!error) return i18n.t('company_settings.general.employment_data.modal.error')
    return (
      ('field' in error ? error.messages[0] : error.message) ||
      i18n.t('company_settings.general.employment_data.modal.error')
    )
  }

  const form = useForm<WorkConditionFormData>({
    defaultValues: {
      name: '',
      description: '',
      role: null, // value null represents the "all" option (added manually).
      workplace: null, // value null represents the "all" option (added manually).
    },
    validate: (data) => {
      const errors: FormErrors<WorkConditionFormData> = {}

      if (!data.name) {
        errors.name = i18n.t('common.empty')
      }

      return errors
    },
    onSubmit: async (values) => {
      form.setSubmitting(true)
      const result = await createWorkingCondition({
        variables: {
          companyId: company.id,
          name: values.name,
          description: values.description,
          applyTo: [
            {
              locationId: values.workplace ? parseInt(values.workplace) : null,
              jobCatalogLevelId: values.role ? parseInt(values.role) : null,
            },
          ],
        },
      })
      const handled = await handleResult(result, (errors) => {
        form.setErrors(errors)
      })
      form.setSubmitting(false)

      if (handled.success && handled.data.version?.id) {
        app.setFlash({
          type: 'correct',
          message: i18n.t(
            'company_settings.general.employment_data.work_conditions.messages.work_condition_created_success'
          ),
        })

        navigate(
          navigation.settings.contracts_settings.workingConditions.version.conditions.fullPath({
            identifier: handled.data.version?.id,
          })
        )
      } else if (!handled.success && handled.errors) {
        app.setFlash({
          type: 'wrong',
          message: getErrorMessage(handled.errors),
        })
      }

      return handled
    },
  })

  return (
    <Modal
      illustration="checks"
      title={i18n.t('company_settings.general.employment_data.modal.title')}
      description={i18n.t('company_settings.general.employment_data.modal.description')}
      onClose={onClose}
      footer={
        <>
          <Button type="secondary" onClick={onClose}>
            {i18n.t('common.cancel')}
          </Button>
          <Button
            disabled={!form.isValid}
            loading={form.isSubmitting}
            onClick={() => form.handleSubmit()}
          >
            {i18n.t('common.create')}
          </Button>
        </>
      }
    >
      <Box gap="s24">
        <TextField
          label={i18n.t(
            'company_settings.general.employment_data.work_conditions.fields.name.label'
          )}
          placeholder={i18n.t(
            'company_settings.general.employment_data.work_conditions.fields.name.placeholder'
          )}
          value={form.values.name}
          onChange={(newValue) => form.setFieldValue('name', newValue)}
          error={form.touched.name ? form.errors.name : undefined}
        />
        <TextArea
          label={i18n.t(
            'company_settings.general.employment_data.work_conditions.fields.description.label'
          )}
          placeholder={i18n.t(
            'company_settings.general.employment_data.work_conditions.fields.description.placeholder'
          )}
          value={form.values.description}
          onChange={(newValue) => form.setFieldValue('description', newValue)}
          error={form.touched.description ? form.errors.description : undefined}
        />
        <Box background="grey200" padding="s16" borderRadius={{ all: 'abs016' }}>
          <Row>
            <Box padding="s8">
              <Text size="200" weight="semibold">
                {i18n.t(
                  'company_settings.general.employment_data.work_conditions.fields.matching_criteria.label'
                )}
              </Text>
            </Box>
            <Popover.ItemList
              attachment="bottom center"
              toggle={toggle}
              open={open}
              gapDivider={false}
              title={i18n.t(
                'company_settings.general.employment_data.work_conditions.fields.matching_criteria.description'
              )}
            >
              <Box paddingY="s8">
                <F0Icon icon={InfoCircleLine} />
              </Box>
            </Popover.ItemList>
          </Row>
          <Row>
            <Row.Col halfWidth>
              <Box padding="s8">
                <Selector.Popover
                  label={i18n.t(
                    'company_settings.general.employment_data.work_conditions.fields.role.label'
                  )}
                  value={[form.values.role]}
                  onChange={(newValue) => {
                    form.setFieldValue('role', newValue)
                  }}
                  placeholder={i18n.t(
                    'company_settings.general.employment_data.work_conditions.fields.role.placeholder'
                  )}
                  blankLabel={i18n.t(
                    'company_settings.general.employment_data.work_conditions.fields.role.blanklabel'
                  )}
                >
                  {roleOptions}
                </Selector.Popover>
              </Box>
            </Row.Col>
            <Row.Col halfWidth>
              <Box padding="s8">
                <Selector.Popover
                  label={i18n.t(
                    'company_settings.general.employment_data.work_conditions.fields.workplace.label'
                  )}
                  value={[form.values.workplace]}
                  onChange={(newValue) => {
                    form.setFieldValue('workplace', newValue)
                  }}
                  placeholder={i18n.t(
                    'company_settings.general.employment_data.work_conditions.fields.workplace.placeholder'
                  )}
                  blankLabel={i18n.t(
                    'company_settings.general.employment_data.work_conditions.fields.workplace.blanklabel'
                  )}
                >
                  {workplaceOptions}
                </Selector.Popover>
              </Box>
            </Row.Col>
          </Row>
        </Box>
      </Box>
    </Modal>
  )
}

export default CreateWorkingConditionModal
